import type { BearerTokenKind } from '@/auth/types/auth.types';

/**
A UUID path segment — the shape every ADP entity identifier takes.
 */
const uuidSegmentPattern = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i;

/**
Leading `scheme://host[:port]` of an absolute URL.
 */
const originPattern = /^[a-z][\d+.a-z-]*:\/\/[^/]*/i;

/**
 * Reduces a request URL or a router path to a form that is safe to ship to Sentry:
 * origin, query string and fragment dropped, UUID segments replaced with `:id`.
 *
 * Every part removed here is a known carrier of identifying data. Query strings hold
 * the login page's `?email=` invitation deep link, the `returnUrl` destination and
 * search terms; path segments hold entity ids — including the user's own `sub` in
 * `user-profile/details/by-user/{sub}`. Redacting the ids also collapses per-entity
 * variants of one endpoint into a single Sentry title.
 *
 * Pure string handling rather than `new URL()`: it takes both the absolute ADP request
 * URLs and the relative router URLs from `getCurrentUrl()`, and it cannot throw —
 * building this error must never mask the failure it describes.
 *
 * @param {string} urlOrPath - An absolute URL or a router path.
 * @returns {string} The redacted path.
 */
const toSafePath = (urlOrPath: string): string => {
  const withoutOrigin = urlOrPath.replace(originPattern, '');
  const withoutQueryAndFragment = withoutOrigin.split('?', 1)[0]?.split('#', 1)[0] ?? '';

  return withoutQueryAndFragment
    .split('/')
    .map((segment) => (uuidSegmentPattern.test(segment) ? ':id' : segment))
    .join('/');
};

export class MissingBearerTokenError extends Error {
  public readonly path: string;

  public readonly route: string;

  public constructor(
    public readonly tokenKind: BearerTokenKind,
    requestUrl: string,
    currentRoute: string,
  ) {
    const path = toSafePath(requestUrl);
    const route = toSafePath(currentRoute);
    super(`No ${tokenKind} token available for ${path} (route: ${route})`);
    this.name = 'MissingBearerTokenError';
    this.path = path;
    this.route = route;
  }
}
